/**
 * Descriptions riches (HTML) des événements : filtre en liste blanche côté
 * affichage. Le parsing via DOMParser est inerte (aucun script exécuté,
 * aucune ressource chargée) ; seules les balises/attributs sûrs sont recréés.
 */

const ALLOWED_TAGS = new Set([
  "p", "br", "strong", "b", "em", "i", "u", "s", "strike",
  "h2", "h3", "h4", "ul", "ol", "li", "blockquote", "a", "div",
]);
const CANONICAL = { b: "strong", i: "em", strike: "s" };
const DROP_WITH_CONTENT = new Set([
  "script", "style", "iframe", "object", "embed", "noscript", "template",
  "svg", "math", "textarea", "select", "title", "head", "meta", "link",
]);
const ALIGNABLE = new Set(["p", "div", "h2", "h3", "h4", "li", "blockquote"]);
const SAFE_ALIGN = /^(left|center|right|justify)$/i;
const SAFE_HREF = /^(https?:\/\/|mailto:|tel:)/i;

export function isRichText(value) {
  return (
    !!value &&
    /<(p|br|div|ul|ol|li|h[2-4]|strong|b|em|i|u|s|blockquote|a)\b[^>]*>/i.test(value)
  );
}

function cleanChildren(source, target, doc) {
  source.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      target.appendChild(doc.createTextNode(child.textContent || ""));
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return;
    const name = child.tagName.toLowerCase();
    if (DROP_WITH_CONTENT.has(name)) return;
    if (!ALLOWED_TAGS.has(name)) {
      cleanChildren(child, target, doc);
      return;
    }
    const tag = CANONICAL[name] || name;
    const out = doc.createElement(tag);
    if (tag === "a") {
      const href = (child.getAttribute("href") || "").trim();
      if (SAFE_HREF.test(href)) {
        out.setAttribute("href", href);
        out.setAttribute("target", "_blank");
        out.setAttribute("rel", "noopener noreferrer nofollow");
      }
    }
    if (ALIGNABLE.has(tag)) {
      const align = child.style.textAlign || child.getAttribute("align") || "";
      if (SAFE_ALIGN.test(align)) out.setAttribute("style", `text-align: ${align.toLowerCase()}`);
    }
    if (tag !== "br") cleanChildren(child, out, doc);
    target.appendChild(out);
  });
}

export function sanitizeRichHtml(html) {
  if (!html || typeof window === "undefined" || typeof DOMParser === "undefined") return "";
  const parsed = new DOMParser().parseFromString(html, "text/html");
  const doc = document.implementation.createHTMLDocument("");
  const container = doc.createElement("div");
  cleanChildren(parsed.body, container, doc);
  return container.innerHTML.trim();
}

/** Version texte (cartes, recherche, extraits). */
export function richTextToPlain(value) {
  if (!value) return "";
  if (!isRichText(value)) return String(value).trim();
  const withBreaks = String(value)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|h[2-4]|li|blockquote)>/gi, "$&\n");
  if (typeof DOMParser === "undefined") return withBreaks.replace(/<[^>]*>/g, "").trim();
  const doc = new DOMParser().parseFromString(withBreaks, "text/html");
  return (doc.body.textContent || "").replace(/\n\s*\n+/g, "\n").trim();
}
